import React from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { Phone, Email, Person } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { stitchColors } from '../theme/tokens';
import { AssociatedLawyerProfile } from '../types/lawyer';

/**
 * Read-only card shown in the Dashboard "Plan & profile" column for EO users.
 * Lists the minimal profile of each currently-associated Estate Lawyer.
 */
const AssociatedLawyersCard: React.FC = () => {
  const { user } = useAuthStore();
  const { data: lawyers = [], isLoading, isError } = useQuery<AssociatedLawyerProfile[]>({
    queryKey: ['associatedLawyers', user?.id],
    queryFn: async () => {
      const response = await api.get<AssociatedLawyerProfile[]>('/lawyers/associated');
      return response.data;
    },
    enabled: !!user?.id,
  });

  return (
    <Box
      sx={{
        border: `1px solid ${stitchColors.outlineVariant}`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: stitchColors.surfaceContainerLowest,
      }}
    >
      <Box
        sx={{
          bgcolor: stitchColors.surface,
          px: 2,
          py: 2,
          borderBottom: `1px solid ${stitchColors.outlineVariant}`,
        }}
      >
        <Typography variant="h3" component="h2" sx={{ fontSize: '1.125rem' }}>
          Associated Lawyers
        </Typography>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Typography sx={{ fontSize: '0.875rem', color: stitchColors.error }}>
            Could not load associated lawyers. Please try refreshing.
          </Typography>
        ) : lawyers.length === 0 ? (
          <Typography sx={{ fontSize: '0.875rem', color: stitchColors.onSurfaceVariant }}>
            No lawyers associated yet. Use the Users page to associate an Estate Lawyer with your account.
          </Typography>
        ) : (
          <Box>
            {lawyers.map((lawyer, index) => (
              <React.Fragment key={lawyer.id}>
                {index > 0 && <Divider sx={{ my: 1.5 }} />}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: stitchColors.surfaceVariant,
                      color: stitchColors.onSurfaceVariant,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Person sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                      {lawyer.name || '(No name)'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Email sx={{ fontSize: 13, color: stitchColors.onSurfaceVariant }} />
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          color: stitchColors.onSurfaceVariant,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {lawyer.email}
                      </Typography>
                    </Box>
                    {lawyer.phoneNumber && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Phone sx={{ fontSize: 13, color: stitchColors.onSurfaceVariant }} />
                        <Typography sx={{ fontSize: '0.8125rem', color: stitchColors.onSurfaceVariant }}>
                          {lawyer.phoneNumber}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AssociatedLawyersCard;
